// src/LoginPage.jsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from './firebase';
import { useAuth } from './AuthContext';

// 에러 코드 → 사용자 안내 메시지 + 해결 방법
const ERROR_MESSAGES = {
  'auth/unauthorized-domain': {
    msg: '이 도메인은 Firebase에 등록되지 않았습니다.',
    fix: 'Firebase Console → Authentication → Settings → Authorized domains 에 현재 도메인(localhost)을 추가하세요.',
  },
  'auth/popup-blocked': {
    msg: '팝업이 차단되었습니다.',
    fix: '브라우저 주소창 우측의 팝업 차단 아이콘을 클릭해 허용해 주세요.',
  },
  'auth/internal-error': {
    msg: 'Firebase 내부 오류입니다.',
    fix: '.env.local의 Firebase 설정값(API Key 등)이 올바른지 확인해 주세요.',
  },
  'auth/api-key-not-valid': {
    msg: 'API Key가 유효하지 않습니다.',
    fix: '.env.local의 REACT_APP_FIREBASE_API_KEY 값을 Firebase Console에서 다시 복사해 주세요.',
  },
  'auth/configuration-not-found': {
    msg: 'Firebase 프로젝트 설정을 찾을 수 없습니다.',
    fix: '.env.local의 Project ID / Auth Domain이 올바른지 확인하세요.',
  },
  'auth/network-request-failed': {
    msg: '네트워크 오류가 발생했습니다.',
    fix: '인터넷 연결을 확인하고 다시 시도해 주세요.',
  },
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null); // { msg, fix, code }

  React.useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const from = location.state?.from?.pathname || '/';

  const handleGoogleLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
      navigate(from, { replace: true });
    } catch (err) {
      console.error('[Login Error]', err.code, err.message);

      if (err.code === 'auth/popup-closed-by-user' ||
          err.code === 'auth/cancelled-popup-request') {
        // 사용자가 직접 닫은 경우 — 에러 표시 안 함
        setLoading(false);
        return;
      }

      const known = ERROR_MESSAGES[err.code];
      if (known) {
        setError({ ...known, code: err.code });
      } else {
        setError({
          msg: `로그인 오류: ${err.code || '알 수 없는 오류'}`,
          fix: '브라우저 콘솔(F12 → Console 탭)에서 상세 오류를 확인하세요.',
          code: err.code,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        {/* 로고 */}
        <div style={s.logoArea}>
          <div style={s.logoIcon}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <rect x="3"  y="3"  width="7" height="7" rx="1.5" fill="#1B4A8C" opacity="0.9"/>
              <rect x="14" y="3"  width="7" height="7" rx="1.5" fill="#1B4A8C" opacity="0.5"/>
              <rect x="3"  y="14" width="7" height="7" rx="1.5" fill="#1B4A8C" opacity="0.5"/>
              <rect x="14" y="14" width="7" height="7" rx="1.5" fill="#1B4A8C" opacity="0.2"/>
            </svg>
          </div>
          <div style={s.courseName}>이러닝 교수설계 입문</div>
        </div>

        <div style={s.heading}>수강생 로그인</div>
        <div style={s.subtext}>
          등록된 Google 계정으로 로그인하세요.<br />
          수강 등록이 필요하시면 담당자에게 문의해 주세요.
        </div>

        {/* 에러 메시지 — 코드별 구체적 안내 */}
        {error && (
          <div style={s.errorBox}>
            <div style={s.errorMsg}>{error.msg}</div>
            <div style={s.errorFix}>💡 {error.fix}</div>
            {error.code && (
              <div style={s.errorCode}>오류 코드: {error.code}</div>
            )}
          </div>
        )}

        <button
          style={{ ...s.googleBtn, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
          onClick={handleGoogleLogin}
          disabled={loading}
        >
          {loading ? <span style={s.spinner} /> : <GoogleIcon />}
          {loading ? '로그인 중...' : 'Google 계정으로 로그인'}
        </button>

        <div style={s.notice}>등록된 Google 계정만 접속할 수 있습니다</div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
      <path d="M3.964 10.706c-.18-.54-.282-1.117-.282-1.706s.102-1.166.282-1.706V4.962H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.038l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.962L3.964 6.294C4.672 4.167 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

const s = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F7F6F4', padding: '24px 16px' },
  card: { width: '100%', maxWidth: '400px', background: '#FFFFFF', border: '0.5px solid #E2E0DB', borderRadius: '14px', padding: '40px 36px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  logoArea: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '28px' },
  logoIcon: { width: '52px', height: '52px', background: '#EBF1FA', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  courseName: { fontSize: '13px', fontWeight: '500', color: '#9898AA', letterSpacing: '0.02em' },
  heading: { fontSize: '20px', fontWeight: '500', color: '#1A1A2E', marginBottom: '10px', textAlign: 'center' },
  subtext: { fontSize: '14px', color: '#5C5C72', lineHeight: '1.65', textAlign: 'center', marginBottom: '28px' },
  errorBox: { width: '100%', padding: '12px 14px', background: '#FEF2F2', border: '0.5px solid #FECACA', borderRadius: '8px', marginBottom: '16px' },
  errorMsg: { fontSize: '13px', fontWeight: '500', color: '#B91C1C', marginBottom: '6px' },
  errorFix: { fontSize: '12px', color: '#7F1D1D', lineHeight: '1.6', marginBottom: '4px' },
  errorCode: { fontSize: '11px', color: '#9898AA', fontFamily: 'monospace' },
  googleBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '12px 20px', background: '#FFFFFF', border: '0.5px solid #E2E0DB', borderRadius: '8px', fontSize: '14px', fontWeight: '500', color: '#1A1A2E', fontFamily: 'inherit', marginBottom: '16px' },
  spinner: { display: 'inline-block', width: '16px', height: '16px', border: '2px solid #E2E0DB', borderTopColor: '#1B4A8C', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 },
  notice: { fontSize: '12px', color: '#9898AA', textAlign: 'center' },
};
